import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  deleteTeacherApiKey,
  getTeacherApiKeys,
  postTeacherApiKey,
  type TeacherApiKeySummary,
} from "../auth/api-client";

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function TeacherApiKeysPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken() ?? "";

  const [keys, setKeys] = useState<TeacherApiKeySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const out = await getTeacherApiKeys(bearer);
      setKeys(out.apiKeys);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load API keys");
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(ev: React.FormEvent) {
    ev.preventDefault();
    const lab = label.trim();
    if (!bearer || !lab || creating) return;
    setCreating(true);
    setError(null);
    try {
      const out = await postTeacherApiKey(bearer, { label: lab });
      setLabel("");
      setRevealedSecret(out.plaintextKey);
      setRevealOpen(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create key");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!bearer || revokingId) return;
    const ok = window.confirm("Revoke this API key? Scripts using it will stop working immediately.");
    if (!ok) return;
    setRevokingId(id);
    setError(null);
    try {
      await deleteTeacherApiKey(bearer, id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not revoke key");
    } finally {
      setRevokingId(null);
    }
  }

  function closeReveal() {
    setRevealOpen(false);
    setRevealedSecret(null);
  }

  async function copySecret() {
    if (!revealedSecret || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(revealedSecret);
    } catch {
      setError("Clipboard unavailable. Copy the key manually.");
    }
  }

  return (
    <div className="teacher-page submissions-dashboard">
      <header className="submissions-hero overview-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">Teacher · Developer</p>
          <h1 className="teacher-page-title">API keys</h1>
          <p className="teacher-page-lede">
            Long-lived keys for scripts and tools that call the Code Create Slay HTTP API as you. Use{" "}
            <code className="teacher-inline-code">Authorization: ApiKey …</code> or the{" "}
            <code className="teacher-inline-code">x-api-key</code> header.
          </p>
        </div>
      </header>

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      <section className="teacher-panel">
        <h2 className="teacher-panel-title">Create key</h2>
        <form className="teacher-toolbar-form" onSubmit={(ev) => void handleCreate(ev)}>
          <label className="field teacher-input-grow" style={{ minWidth: "14rem", marginBottom: 0 }}>
            <span className="field-label">Label</span>
            <input
              className="input"
              value={label}
              onChange={(ev) => setLabel(ev.target.value)}
              placeholder="e.g. course import laptop"
              maxLength={200}
              disabled={!bearer || creating}
              required
            />
          </label>
          <button type="submit" className="btn primary" disabled={!bearer || creating}>
            {creating ? "Creating…" : "Generate"}
          </button>
        </form>
        <p className="muted small" style={{ marginTop: "0.75rem" }}>
          You will see the secret once. Store it in a password manager. We only keep a hash server-side.
        </p>
      </section>

      <section className="teacher-panel">
        <h2 className="teacher-panel-title">Active keys</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="muted">No keys yet.</p>
        ) : (
          <ul className="teacher-api-key-list">
            {keys.map((k) => (
              <li key={k.id} className="teacher-api-key-row">
                <div>
                  <div className="teacher-api-key-label">{k.label}</div>
                  <div className="muted small">
                    Created {formatTs(k.createdAt)}
                    {k.lastUsedAt ? ` · Last used ${formatTs(k.lastUsedAt)}` : " · Never used"}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn secondary danger"
                  disabled={revokingId === k.id}
                  onClick={() => void handleRevoke(k.id)}
                >
                  {revokingId === k.id ? "Revoking…" : "Revoke"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {revealOpen && revealedSecret ? (
        <dialog className="teacher-modal" open aria-labelledby="api-key-reveal-title">
          <div className="teacher-modal-card">
            <h2 id="api-key-reveal-title" className="teacher-modal-title">
              Copy your API key
            </h2>
            <p className="muted small">
              This is the only time we show this value. Anyone with this key can act as you on teacher API
              routes until you revoke it.
            </p>
            <pre className="teacher-api-key-secret">{revealedSecret}</pre>
            <div className="teacher-modal-actions">
              <button type="button" className="btn secondary" onClick={() => void copySecret()}>
                Copy
              </button>
              <button type="button" className="btn primary" onClick={() => closeReveal()}>
                Done
              </button>
            </div>
          </div>
        </dialog>
      ) : null}
    </div>
  );
}
